import {
    ArrowMinimize24Regular,
    Bookmark24Regular,
    BrainCircuit24Regular,
    History24Regular,
    Sparkle24Regular
} from "@fluentui/react-icons";
import {
    BrandSparkleIcon,
    CloseIcon,
    ExpandIcon,
    NavChatIcon,
    NewChatIcon
} from "./icons";
import {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useHistory} from "react-router-dom";
import {useSelector} from "react-redux";
import {
    AIChatMessage,
    AIChatProtocolClient,
    AIChatCompletionOperationOptions
} from "@microsoft/ai-chat-protocol";
import applicationApi from "@/features/application/api/applicationAPI";
import {
    DataStatus,
    IColumnMetadata
} from "@/features/application/api/applicationModels";
import insightActionsStore from "@/features/coachInsights/api/insightActionsStore";
import {AppState} from "@/state/rootReducer";
import {getCurrentSelectedBusinessUnitId} from "@/util/stateUtil";
import {sortingDirections} from "@/util/sortUtil";
import hj from "@/tracking/hotjar";
import IntelligenceAgentsView from "./IntelligenceAgentsView";
import IntelligenceConversation from "./IntelligenceConversation";
import IntelligenceHistoryView from "./IntelligenceHistoryView";
import IntelligenceMemoryView from "./IntelligenceMemoryView";
import IntelligencePromptBar from "./IntelligencePromptBar";
import IntelligenceSavedView from "./IntelligenceSavedView";
import IntelligenceWorkflowView from "./IntelligenceWorkflowView";
import type {ActionWorkflowDefinition} from "./workflowModels";
import {buildWorkflowFromDiagnosis} from "./workflowBuilders";
import {ActionCardRef, hydrateActionCards} from "./actionCatalog";
import bentoInsightsDemoAPI from "../../api/bentoInsightsDemoAPI";
import {useAgentSettings} from "../../agentSettingsStore";
import {useBentoIntelligence} from "../../BentoIntelligenceContext";
import {useBentoPersona} from "../../BentoPersonaContext";
import {AGENT_TO_COACH, CATEGORY_TO_PRODUCTS, COACHES} from "../../coachCatalog";
import {useCoachSettings} from "../../coachSettingsStore";
import {
    getAgentCards,
    getAgentIcon,
    insightAgents,
    toAgentIdentity
} from "../../insightAgents";
import type {AgentIdentity} from "../../insightAgents";
import {buildPersonaPromptContext, getPersonaFriendlyName} from "../../bentoPersona";
import {LIVE_CARD_IDS} from "../../liveInsightIds";
import {insightCategories, InsightCard} from "../../insightCardModels";
import {
    Agent,
    agentsSeed,
    buildAssistantReply,
    ConversationMessage,
    DiagnosisReport,
    IntelligenceView,
    MorningBrief,
    MemoryItem,
    memorySeed,
    MessageAction,
    PromptHistoryItem,
    promptHistory,
    SavedPrompt,
    savedPromptsSeed,
    suggestedPrompts,
    SuggestedPrompt,
    InsightTeaser
} from "./intelligenceModels";
import "./par-intelligence-drawer.scss";

interface ParIntelligenceDrawerProps {
    userName?: string;
    isOpen: boolean;
    onClose: () => void;
    /** When provided the drawer opens directly into the workflow composer. */
    initialWorkflow?: ActionWorkflowDefinition | null;
    /** When provided the drawer opens on chat and sends this prompt immediately. */
    initialPrompt?: string | null;
    /** The specialist agent guiding this session, shown throughout the drawer. */
    initialAgent?: AgentIdentity | null;
    /** Bumped by the caller on every open*() call — signals a fresh open request
     * so initialWorkflow/initialPrompt can be re-applied without remounting. */
    openRequestId: number;
}

interface NavEntry {
    id: IntelligenceView;
    label: string;
    icon: JSX.Element;
}

const NAV_ENTRIES: NavEntry[] = [
    {id: "chat", label: "Chat", icon: <NavChatIcon />},
    {id: "history", label: "History", icon: <History24Regular />},
    {id: "saved", label: "Saved", icon: <Bookmark24Regular />},
    {id: "agents", label: "Agents", icon: <BrainCircuit24Regular />},
    {id: "memory", label: "Memory", icon: <Sparkle24Regular />}
];

const TRACK_FEATURE = "feature=ParIntelligence";

const INTELLIGENCE_CHAT_URL = "/api/CoachAssistant/intelligence/chat";

interface IntelligenceDataPointsMetadata {
    columns_metadata: IColumnMetadata[];
    sort_column: string;
    sort_direction: sortingDirections;
}

interface IntelligenceStreamDelta {
    delta?: {content?: string; role?: string};
    context?: {
        followup_questions?: string[] | null;
        action_cards?: ActionCardRef[] | null;
        status?: string | null;
        text_stream_complete?: boolean | null;
        diagnosis_report?: DiagnosisReport | null;
        morning_brief?: MorningBrief | null;
        data_points?: Record<string, unknown> | null;
        data_points_metadata?: IntelligenceDataPointsMetadata | null;
        highchart_data?: string | null;
        file_name?: string | null;
    } | null;
    sessionState?: Record<string, unknown>;
}

// const SUGGESTED_ACTION_COUNT = insightCategories.reduce(
//     (sum, category) =>
//         sum +
//         category.cards.reduce((cardSum, card) => cardSum + card.actions.length, 0),
//     0
// );

const track = (event: string): void => {
    applicationApi.trackPageViewOrAction(window.location.pathname, TRACK_FEATURE);
    hj("event", event);
};

const ParIntelligenceDrawer: FC<ParIntelligenceDrawerProps> = ({
    userName = "Demo User",
    isOpen,
    onClose,
    initialWorkflow = null,
    initialPrompt = null,
    initialAgent = null,
    openRequestId
}) => {
    const [show, setShow] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const hasOpenedBeforeRef = useRef(false);
    const [view, setView] = useState<IntelligenceView>("chat");
    const [messages, setMessages] = useState<ConversationMessage[]>([]);
    const [input, setInput] = useState("");
    const [isThinking, setIsThinking] = useState(false);
    const [savedPrompts, setSavedPrompts] =
        useState<SavedPrompt[]>(savedPromptsSeed);
    const [agents, setAgents] = useState<Agent[]>(agentsSeed);
    const [memory, setMemory] = useState<MemoryItem[]>(memorySeed);
    const [completedActionIds, setCompletedActionIds] = useState<string[]>([]);
    const [activeWorkflow, setActiveWorkflow] =
        useState<ActionWorkflowDefinition | null>(initialWorkflow);

    const {mode} = useBentoIntelligence();
    const {persona} = useBentoPersona();
    const coachSettings = useCoachSettings();
    const businessUnitId = useSelector((state: AppState) =>
        getCurrentSelectedBusinessUnitId(state)
    );
    const isNoCoachesPersona = persona.id === "no_coaches_enabled";
    const isProductEnabled = useCallback(
        (productId: string): boolean =>
            !isNoCoachesPersona && Boolean(coachSettings[productId]),
        [isNoCoachesPersona, coachSettings]
    );

    const personalizedSuggestions = useMemo(
        () =>
            suggestedPrompts
                .filter(
                    (prompt) =>
                        !prompt.personaIds ||
                        prompt.personaIds.includes(
                            isNoCoachesPersona ? "super_user" : persona.id
                        )
                )
                .map((prompt) => ({
                    ...prompt,
                    category: isNoCoachesPersona
                        ? prompt.category
                        : `${prompt.category} · ${getPersonaFriendlyName(persona)}`
                })),
        [isNoCoachesPersona, persona]
    );

    const isSuggestionDisabled = useCallback(
        (prompt: SuggestedPrompt): boolean =>
            Boolean(prompt.requiredProductIds) &&
            !prompt.requiredProductIds?.some(isProductEnabled),
        [isProductEnabled]
    );

    // Mirrors the insight-availability logic on the main Insights page (BentoDemo) so this
    // count stays consistent with what the persona would actually see there. Calls the same
    // demo-insight endpoint per live card — duplicated work, but this is demo-only code.
    const agentSettings = useAgentSettings();
    const [liveInsightCards, setLiveInsightCards] = useState<
        Record<string, InsightCard>
    >({});
    const [liveInsightStatuses, setLiveInsightStatuses] = useState<
        Record<string, DataStatus>
    >({});
    const [assignedInsightKeys, setAssignedInsightKeys] = useState<Set<string>>(
        new Set()
    );

    useEffect(() => {
        if (!businessUnitId || mode !== "live") {
            setLiveInsightCards({});
            setLiveInsightStatuses({});
            return;
        }

        const abortController = new AbortController();

        LIVE_CARD_IDS.forEach(async (insightId) => {
            try {
                const card = await bentoInsightsDemoAPI.getDemoInsight(
                    insightId,
                    businessUnitId,
                    abortController.signal
                );
                setLiveInsightStatuses((previous) => ({
                    ...previous,
                    [insightId]: DataStatus.Loaded
                }));
                if (card) {
                    setLiveInsightCards((previous) => ({
                        ...previous,
                        [insightId]: card
                    }));
                }
            } catch (error) {
                if ((error as Error)?.name !== "AbortError") {
                    setLiveInsightStatuses((previous) => ({
                        ...previous,
                        [insightId]: DataStatus.Failed
                    }));
                }
            }
        });

        return () => abortController.abort();
    }, [businessUnitId, mode]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        let cancelled = false;
        insightActionsStore.listActions().then((actions) => {
            if (!cancelled) {
                setAssignedInsightKeys(
                    new Set(actions.map((action) => action.insightKey))
                );
            }
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    // Same eligibility rules as availableInsightCount used to apply on its own — now also
    // collects the actual surfaced cards (for the empty-state teaser) and whether any
    // still-pending live card means the teaser should show a loading state.
    const {insightTeasers, isInsightsLoading} = useMemo(() => {
        const enabledAgents = insightAgents.filter(
            (agent) => agentSettings[agent.id]?.enabled ?? agent.enabled
        );
        const sourceAgents =
            enabledAgents.length > 0 ? enabledAgents : insightAgents;

        const isCardFromEnabledProduct = (card: InsightCard): boolean => {
            const category = insightCategories.find((candidate) =>
                candidate.cards.some((candidateCard) => candidateCard.id === card.id)
            );
            const productKeys = category
                ? CATEGORY_TO_PRODUCTS[category.id]
                : undefined;
            if (!productKeys || productKeys.length === 0) {
                return true;
            }
            if (isNoCoachesPersona) {
                return false;
            }
            return productKeys.some((key) => coachSettings[key]);
        };

        const teasers: InsightTeaser[] = [];
        let anyPending = false;

        COACHES.forEach((coach) => {
            if (!persona.visibleCoachIds.includes(coach.id)) {
                return;
            }
            const mappedAgents = sourceAgents.filter(
                (agent) => AGENT_TO_COACH[agent.id] === coach.id
            );
            mappedAgents.forEach((agent) => {
                const allCards = getAgentCards(agent);
                const cards =
                    mode === "live"
                        ? allCards.filter((card) => LIVE_CARD_IDS.includes(card.id))
                        : allCards;
                const visibleCards = cards.filter(
                    (card) =>
                        persona.visibleInsightIds.includes(card.id) &&
                        !assignedInsightKeys.has(card.id) &&
                        isCardFromEnabledProduct(card)
                );

                visibleCards.forEach((card) => {
                    if (mode !== "live" || !LIVE_CARD_IDS.includes(card.id)) {
                        teasers.push({card, agent: toAgentIdentity(agent)});
                        return;
                    }
                    const status = liveInsightStatuses[card.id];
                    const liveCard = liveInsightCards[card.id];
                    if (status === DataStatus.Loaded && liveCard) {
                        teasers.push({
                            card: liveCard,
                            agent: toAgentIdentity(agent)
                        });
                    } else if (status === undefined) {
                        anyPending = true;
                    }
                });
            });
        });

        return {insightTeasers: teasers, isInsightsLoading: anyPending};
    }, [
        agentSettings,
        mode,
        persona,
        coachSettings,
        isNoCoachesPersona,
        assignedInsightKeys,
        liveInsightStatuses,
        liveInsightCards
    ]);

    // The specialist guiding this session — from an opened workflow or a card.
    const guidingAgent: AgentIdentity | null = activeWorkflow?.agent ?? initialAgent;
    const AgentIcon = guidingAgent
        ? getAgentIcon(guidingAgent, activeWorkflow?.sourceInsightId)
        : undefined;

    const replyIndex = useRef(0);
    const replyTimer = useRef<number>();
    const messagesRef = useRef<ConversationMessage[]>([]);
    const liveSessionStateRef = useRef<Record<string, unknown>>({});
    const abortControllerRef = useRef<AbortController | null>(null);
    const wasOpenRef = useRef(false);

    const resetDrawerState = useCallback(() => {
        abortControllerRef.current?.abort();
        liveSessionStateRef.current = {};
        replyIndex.current = 0;

        setExpanded(false);
        setView("chat");
        setMessages([]);
        setInput("");
        setIsThinking(false);
        setCompletedActionIds([]);
        setActiveWorkflow(null);
    }, []);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    // const activeAgentCount = useMemo(
    //     () => agents.filter((agent) => agent.enabled).length,
    //     [agents]
    // );

    // Animate in whenever the drawer opens + lock body scroll + track the open
    // event. The component itself stays mounted while closed (see isOpen), so
    // this reacts to the isOpen prop instead of running once on mount.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const frame = requestAnimationFrame(() => setShow(true));
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        track("par_intelligence_opened");

        if (!hasOpenedBeforeRef.current) {
            hasOpenedBeforeRef.current = true;
            setExpanded(true);
        }

        return () => {
            cancelAnimationFrame(frame);
            document.body.style.overflow = previousOverflow;
            if (replyTimer.current) {
                window.clearTimeout(replyTimer.current);
            }
            abortControllerRef.current?.abort();
        };
    }, [isOpen]);

    // Collapse immediately when the drawer is closed programmatically (e.g. a
    // workflow confirms and navigates to a full page). Without this the internal
    // `show` flag stays true and the panel would linger on top of the new page.
    useEffect(() => {
        if (!isOpen) {
            setShow(false);
        }
    }, [isOpen]);

    // Reset active session content after every close so reopening starts from
    // a clean chat/workflow state while preserving longer-lived preferences.
    useEffect(() => {
        if (wasOpenRef.current && !isOpen) {
            resetDrawerState();
        }
        wasOpenRef.current = isOpen;
    }, [isOpen, resetDrawerState]);

    const requestClose = useCallback(() => {
        setShow(false);
        window.setTimeout(onClose, 260);
    }, [onClose]);

    const history = useHistory();
    const handleViewAllInsights = useCallback(() => {
        requestClose();
        history.push("/");
    }, [requestClose, history]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                requestClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, requestClose]);

    const sendPromptLive = useCallback(
        async (text: string, userMessage: ConversationMessage): Promise<void> => {
            setView("chat");
            setMessages((previous) => [...previous, userMessage]);
            setInput("");
            setIsThinking(true);

            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            const history: AIChatMessage[] = messagesRef.current.map((message) => ({
                role: message.role,
                content: message.text
            }));
            const allMessages: AIChatMessage[] = [
                ...history,
                {role: "user", content: text}
            ];

            const options: AIChatCompletionOperationOptions = {
                context: {
                    currentSelectedBusinessUnitId: businessUnitId,
                    isNoCoachesPersona
                },
                sessionState: liveSessionStateRef.current,
                abortSignal: controller.signal
            };

            const assistantId = `ai_${Date.now()}`;
            let answer = "";
            let assistantAdded = false;
            let followUps: string[] | undefined;
            let actions: MessageAction[] | undefined;
            const activity: string[] = [];
            let isComplete = false;
            let diagnosisReport: DiagnosisReport | undefined;
            let morningBrief: MorningBrief | undefined;
            let dataPoints: Record<string, unknown> | undefined;
            let columnsMetadata: IColumnMetadata[] | undefined;
            let sortColumn: string | undefined;
            let sortDirection: sortingDirections | undefined;
            let highchart: string | undefined | null;
            let fileName: string | undefined | null;

            const upsertAssistant = (): void => {
                setMessages((previous) => {
                    const next = [...previous];
                    const existingIndex = next.findIndex(
                        (message) => message.id === assistantId
                    );
                    const assistantMessage: ConversationMessage = {
                        id: assistantId,
                        role: "assistant",
                        text: answer,
                        timestamp: "Just now",
                        followUps,
                        actions,
                        activity: activity.length > 0 ? [...activity] : undefined,
                        isComplete,
                        diagnosisReport,
                        morningBrief,
                        dataPoints,
                        columnsMetadata,
                        sortColumn,
                        sortDirection,
                        highchart,
                        fileName
                    };
                    if (existingIndex >= 0) {
                        next[existingIndex] = assistantMessage;
                    } else {
                        next.push(assistantMessage);
                    }
                    return next;
                });
            };

            try {
                const client = new AIChatProtocolClient(INTELLIGENCE_CHAT_URL);
                const result = (await client.getStreamedCompletion(
                    allMessages,
                    options
                )) as AsyncIterable<IntelligenceStreamDelta>;

                for await (const response of result) {
                    if (controller.signal.aborted) {
                        break;
                    }
                    if (response.context?.followup_questions) {
                        followUps = response.context.followup_questions;
                    }
                    if (response.context?.action_cards) {
                        actions = hydrateActionCards(response.context.action_cards);
                    }
                    if (response.context?.diagnosis_report) {
                        diagnosisReport = response.context.diagnosis_report;
                        if (!assistantAdded) {
                            assistantAdded = true;
                            setIsThinking(false);
                        }
                        upsertAssistant();
                    }
                    if (response.context?.morning_brief) {
                        morningBrief = response.context.morning_brief;
                        if (!assistantAdded) {
                            assistantAdded = true;
                            setIsThinking(false);
                        }
                        upsertAssistant();
                    }
                    if (response.context?.data_points) {
                        dataPoints = response.context.data_points;
                        columnsMetadata =
                            response.context.data_points_metadata?.columns_metadata;
                        sortColumn =
                            response.context.data_points_metadata?.sort_column;
                        sortDirection =
                            response.context.data_points_metadata?.sort_direction;
                        highchart = response.context.highchart_data;
                        fileName = response.context.file_name;
                        if (!assistantAdded) {
                            assistantAdded = true;
                            setIsThinking(false);
                        }
                        upsertAssistant();
                    }
                    if (response.context?.status) {
                        activity.push(response.context.status);
                        if (!assistantAdded) {
                            assistantAdded = true;
                            setIsThinking(false);
                        }
                        upsertAssistant();
                    }
                    if (response.context?.text_stream_complete) {
                        isComplete = true;
                        upsertAssistant();
                    }
                    if (response.sessionState) {
                        liveSessionStateRef.current = response.sessionState;
                    }
                    if (response.delta?.content) {
                        answer += response.delta.content;
                        if (!assistantAdded) {
                            assistantAdded = true;
                            setIsThinking(false);
                        }
                        upsertAssistant();
                    }
                }

                if (!controller.signal.aborted) {
                    isComplete = true;
                    if (!assistantAdded) {
                        assistantAdded = true;
                    }
                    upsertAssistant();
                }
            } catch {
                if (!controller.signal.aborted) {
                    setMessages((previous) => [
                        ...previous,
                        {
                            id: `ai_error_${Date.now()}`,
                            role: "assistant",
                            text: "Something went wrong reaching PAR Intelligence. Please try again.",
                            timestamp: "Just now"
                        }
                    ]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsThinking(false);
                }
            }
        },
        [businessUnitId, isNoCoachesPersona]
    );

    const sendPrompt = useCallback(
        (prompt: string): void => {
            const text = prompt.trim();
            if (text.length === 0) {
                return;
            }
            const effectivePrompt = buildPersonaPromptContext(text, persona);

            const userMessage: ConversationMessage = {
                id: `user_${Date.now()}`,
                role: "user",
                text,
                timestamp: "Just now"
            };

            if (mode === "live") {
                void sendPromptLive(effectivePrompt, userMessage);
                return;
            }

            setView("chat");
            setMessages((previous) => [...previous, userMessage]);
            setInput("");
            setIsThinking(true);

            const currentIndex = replyIndex.current;
            replyIndex.current += 1;

            replyTimer.current = window.setTimeout(() => {
                setMessages((previous) => [
                    ...previous,
                    buildAssistantReply(effectivePrompt, currentIndex)
                ]);
                setIsThinking(false);
            }, 850);
        },
        [mode, persona, sendPromptLive]
    );

    const handleSend = useCallback(() => {
        track("par_intelligence_prompt_sent");
        sendPrompt(input);
    }, [input, sendPrompt]);

    // Apply a fresh open request (e.g. launching a workflow from an insight card,
    // or a pre-seeded prompt from the Report Builder AI bar). Keyed on
    // openRequestId — bumped by the caller on every open*() call — rather than
    // mount, since the drawer component remains mounted between openings.
    const lastAppliedRequestId = useRef<number | null>(null);
    useEffect(() => {
        if (openRequestId === lastAppliedRequestId.current) {
            return;
        }
        lastAppliedRequestId.current = openRequestId;

        if (initialWorkflow) {
            setActiveWorkflow(initialWorkflow);
        }

        const seed = initialPrompt?.trim();
        if (seed) {
            sendPrompt(seed);
        }
    }, [openRequestId, initialWorkflow, initialPrompt, sendPrompt]);

    const handleRunSuggested = useCallback(
        (suggestion: SuggestedPrompt) => {
            track("par_intelligence_suggested_run");
            sendPrompt(suggestion.prompt);
        },
        [sendPrompt]
    );

    const handleFollowUp = useCallback(
        (prompt: string) => {
            track("par_intelligence_followup_run");
            sendPrompt(prompt);
        },
        [sendPrompt]
    );

    const handleRunAction = useCallback((action: MessageAction) => {
        track("par_intelligence_action_run");
        setCompletedActionIds((previous) =>
            previous.includes(action.id) ? previous : [...previous, action.id]
        );
        const confirmation: ConversationMessage = {
            id: `act_done_${Date.now()}`,
            role: "assistant",
            text: action.result,
            timestamp: "Just now",
            sources: [`Action taken · ${action.category}`]
        };
        setMessages((previous) => [...previous, confirmation]);
    }, []);

    const handleRunSaved = useCallback(
        (prompt: SavedPrompt) => {
            track("par_intelligence_saved_run");
            sendPrompt(prompt.prompt);
        },
        [sendPrompt]
    );

    const handleSavePrompt = useCallback(() => {
        const text = input.trim();
        if (text.length === 0) {
            return;
        }
        track("par_intelligence_prompt_saved");
        const newPrompt: SavedPrompt = {
            id: `sv_${Date.now()}`,
            icon: "sparkle",
            category: "Saved by you",
            title: text.length > 48 ? `${text.slice(0, 48)}…` : text,
            prompt: text
        };
        setSavedPrompts((previous) => [newPrompt, ...previous]);
        setView("saved");
    }, [input]);

    const handleDeleteSaved = useCallback((id: string) => {
        setSavedPrompts((previous) => previous.filter((item) => item.id !== id));
    }, []);

    const handleToggleAgent = useCallback((id: string) => {
        track("par_intelligence_agent_toggled");
        setAgents((previous) =>
            previous.map((agent) =>
                agent.id === id
                    ? {
                          ...agent,
                          enabled: !agent.enabled,
                          status: !agent.enabled
                              ? agent.schedule
                                  ? "scheduled"
                                  : "running"
                              : "paused"
                      }
                    : agent
            )
        );
    }, []);

    const handleAddMemory = useCallback((text: string) => {
        track("par_intelligence_memory_added");
        const newItem: MemoryItem = {
            id: `m_${Date.now()}`,
            category: "Preference",
            text,
            source: "You told PAR Intelligence"
        };
        setMemory((previous) => [newItem, ...previous]);
    }, []);

    const handleRemoveMemory = useCallback((id: string) => {
        setMemory((previous) => previous.filter((item) => item.id !== id));
    }, []);

    const handleSelectHistory = useCallback(
        (item: PromptHistoryItem) => {
            track("par_intelligence_history_opened");
            sendPrompt(item.title);
        },
        [sendPrompt]
    );

    const handleNewChat = useCallback(() => {
        track("par_intelligence_new_chat");
        abortControllerRef.current?.abort();
        liveSessionStateRef.current = {};
        setMessages([]);
        setInput("");
        setIsThinking(false);
        setCompletedActionIds([]);
        setView("chat");
    }, []);

    const handleLaunchDiagnosisWorkflow = useCallback((report: DiagnosisReport) => {
        track("par_intelligence_diagnosis_workflow_launched");
        setActiveWorkflow(buildWorkflowFromDiagnosis(report));
    }, []);

    const renderView = (): JSX.Element => {
        switch (view) {
            case "history":
                return (
                    <IntelligenceHistoryView
                        items={promptHistory}
                        onSelect={handleSelectHistory}
                    />
                );
            case "saved":
                return (
                    <IntelligenceSavedView
                        prompts={savedPrompts}
                        onRun={handleRunSaved}
                        onDelete={handleDeleteSaved}
                    />
                );
            case "agents":
                return (
                    <IntelligenceAgentsView
                        agents={agents}
                        onToggle={handleToggleAgent}
                    />
                );
            case "memory":
                return (
                    <IntelligenceMemoryView
                        items={memory}
                        onAdd={handleAddMemory}
                        onRemove={handleRemoveMemory}
                    />
                );
            default:
                return (
                    <div className="pi-chat">
                        <IntelligenceConversation
                            messages={messages}
                            isThinking={isThinking}
                            userName={userName}
                            agent={guidingAgent}
                            persona={persona}
                            insightTeasers={insightTeasers}
                            isInsightsLoading={isInsightsLoading}
                            onViewAllInsights={handleViewAllInsights}
                            suggestions={personalizedSuggestions}
                            isSuggestionDisabled={isSuggestionDisabled}
                            onRunSuggested={handleRunSuggested}
                            onFollowUp={handleFollowUp}
                            onRunAction={handleRunAction}
                            completedActionIds={completedActionIds}
                            onLaunchDiagnosisWorkflow={handleLaunchDiagnosisWorkflow}
                            expanded={expanded}
                            promptValue={input}
                            promptDisabled={isThinking}
                            onPromptChange={setInput}
                            onSendPrompt={handleSend}
                            onSavePrompt={handleSavePrompt}
                        />
                        {messages.length > 0 && (
                            <IntelligencePromptBar
                                value={input}
                                disabled={isThinking}
                                onChange={setInput}
                                onSend={handleSend}
                                onSave={handleSavePrompt}
                            />
                        )}
                    </div>
                );
        }
    };

    // Not open and not mid-close-animation — skip rendering entirely.
    if (!isOpen && !show) {
        return null;
    }

    return createPortal(
        <div
            className={`pi-backdrop${show ? " pi-backdrop--open" : ""}`}
            onMouseDown={requestClose}
        >
            <div
                className={`pi-drawer${show ? " pi-drawer--open" : ""}${
                    expanded ? " pi-drawer--expanded" : ""
                }`}
                role="dialog"
                aria-modal="true"
                aria-label="PAR Intelligence"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header className="pi-drawer__header">
                    <div className="pi-drawer__brand">
                        <span
                            className={`pi-drawer__brand-mark${
                                guidingAgent ? " pi-drawer__brand-mark--agent" : ""
                            }`}
                            style={
                                guidingAgent
                                    ? ({
                                          "--par-agent": "#8c7bff"
                                      } as React.CSSProperties)
                                    : undefined
                            }
                        >
                            {AgentIcon ? <AgentIcon /> : <BrandSparkleIcon />}
                        </span>
                        <div className="pi-drawer__titles">
                            <h2 className="pi-drawer__title">
                                {guidingAgent
                                    ? guidingAgent.name
                                    : "PAR Intelligence"}
                            </h2>
                            <span className="pi-drawer__subtitle">
                                {guidingAgent
                                    ? guidingAgent.specialty
                                    : `viewing as ${persona.intelligenceRole}`}
                            </span>
                        </div>
                    </div>

                    <div className="pi-drawer__header-actions">
                        <button
                            type="button"
                            className="pi-drawer__icon-btn"
                            aria-label="New chat"
                            onClick={handleNewChat}
                        >
                            <NewChatIcon />
                        </button>
                        <button
                            type="button"
                            className="pi-drawer__icon-btn"
                            aria-label={expanded ? "Collapse" : "Expand"}
                            onClick={() => setExpanded((previous) => !previous)}
                        >
                            {expanded ? <ArrowMinimize24Regular /> : <ExpandIcon />}
                        </button>
                        <button
                            type="button"
                            className="pi-drawer__icon-btn"
                            aria-label="Close"
                            onClick={requestClose}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                </header>

                <div className="pi-drawer__body">
                    {activeWorkflow ? (
                        <IntelligenceWorkflowView
                            definition={activeWorkflow}
                            businessUnitId={businessUnitId}
                            onBack={() => setActiveWorkflow(null)}
                        />
                    ) : (
                        <>
                            <nav
                                className="pi-nav"
                                aria-label="PAR Intelligence sections"
                            >
                                {NAV_ENTRIES.map((entry) => (
                                    <button
                                        type="button"
                                        key={entry.id}
                                        className={`pi-nav__item${
                                            view === entry.id
                                                ? " pi-nav__item--active"
                                                : ""
                                        }`}
                                        aria-current={
                                            view === entry.id ? "page" : undefined
                                        }
                                        onClick={() => setView(entry.id)}
                                    >
                                        {entry.icon}
                                        {entry.label}
                                        {/* {entry.id === "agents" &&
                                            activeAgentCount > 0 && (
                                                <span className="pi-nav__badge">
                                                    {activeAgentCount}
                                                </span>
                                            )} */}
                                        {/* {entry.id === "workflows" &&
                                            SUGGESTED_ACTION_COUNT > 0 && (
                                                <span className="pi-nav__badge">
                                                    {SUGGESTED_ACTION_COUNT}
                                                </span>
                                            )} */}
                                    </button>
                                ))}
                            </nav>

                            <div className="pi-main">{renderView()}</div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ParIntelligenceDrawer;
